import type { AudioEngine } from '../audio/audioEngine.js';
import type { StartupOverlay } from '../hud/startupOverlay.js';
import type { SceneBundle } from '../render/scene.js';
import type { ShipController } from '../sim/shipController.js';
import { effectLevels } from './apply.js';
import { settingsStore } from './store.js';
import type { SettingsState } from './types.js';

interface RuntimeTargets {
  audio: AudioEngine;
  overlay: StartupOverlay;
  scene: SceneBundle;
  ship: ShipController;
}

export const bindRuntimeSettings = (targets: RuntimeTargets): (() => void) => {
  let previous: SettingsState | null = null;
  const apply = (settings: SettingsState): void => {
    applyRuntime(targets, settings, previous);
    previous = settings;
  };
  apply(settingsStore.get());
  return settingsStore.subscribe(apply);
};

const applyRuntime = (
  targets: RuntimeTargets,
  settings: SettingsState,
  previous: SettingsState | null,
): void => {
  const effects = effectLevels(settings);
  targets.audio.applySettings(settings.audio);
  targets.overlay.setHintsEnabled(settings.gameplay.hintsEnabled);
  targets.scene.setRenderScale(settings.display.renderScale);
  targets.scene.bloom.strength = settings.graphics.bloomEnabled && !settings.accessibility.disableScreenFx
    ? 0.6
    : 0;
  if (targets.scene.postFx.uniforms.uGrain) {
    targets.scene.postFx.uniforms.uGrain.value = effects.filmGrain;
  }
  if (targets.scene.postFx.uniforms.uVignette) {
    targets.scene.postFx.uniforms.uVignette.value = effects.vignette;
  }
  if (previous?.gameplay.flightAssistDefault !== settings.gameplay.flightAssistDefault) {
    targets.ship.assist = settings.gameplay.flightAssistDefault;
    targets.ship.rotAssist = settings.gameplay.flightAssistDefault;
  }
};
